import { useFormik } from 'formik';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import Select from 'react-select';
import { Card, CardFooter } from '@components/card/card';
import { CardBody, Form, InputGroup, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useLookupCurrency from '@hooks/Lookups/use-Lookup-Currency';
import Breadcrumb from '@components/Shared/Breadcrumb';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import SwalAlert from '@components/Shared/Alert/SwalAlert';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Loading from '@components/Shared/Loader/Loading';
import { addApInvoices } from '@store/Finance/AP/ApInvoices/ApInvoicesSlice';
import useGetSuppliersList from '@hooks/NTX/Peoples/useGetSuppliersList';
import { TreeSelect } from 'antd';
import useGetPaymentTermsList from '@hooks/Sales/useGetPaymentTermsList';
import useGetPaymentMethod from '@hooks/Payrolls/use-Get-PaymentMethod';
import ApPaymentSchedules from './ApPaymentSchedules';
import ApInvoiceLines from './ApInvoiceLines';
import { AddApInvoiceSchema } from '../../../ValidationForm/validationSchema';
import { useEffect, useState } from 'react';
import ToastSwal from '@components/Shared/Alert/ToastSwal';
import useGetUnApplyPrepayments from '@hooks/Finance/ApPayments/useGetUnApplyPrepayments';
import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';
import { activeFinanceSelector } from '@store/Login/authSelectors';
import ApDebitMemoApplication from './ApDebitMemoApplication';
import { useDailyRate } from '@hooks/Finance/DailyRates/useDailyRate';

const AddApInvoice = () => {
  const activeFinance = useAppSelector(activeFinanceSelector);
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [params] = useSearchParams();
  const invoiceTypeParam = params.get('invoiceType'); // "Distributed"
  const Is_Distributed_Invoice = invoiceTypeParam === 'Distributed';

  const [showModal, setShowModal] = useState(true);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedOperatingUnit, setSelectedOperatingUnit] = useState(null);
  const [selectedDistributedFlag, setSelectedDistributedFlag] = useState<boolean | null>(false); // جديد: boolean
  const [activeTab, setActiveTab] = useState('apInvoiceLines');

  // البيانات المحملة من الـ hooks
  const { Currency, loading: currencyLoading, error: currencyError } = useLookupCurrency();
  const { OperatingUnitOptions, loading: ouLoading, error: ouError } = useGetOperatingUnitWithPermission();
  const { treeSuppliersData, loading: suppliersLoading, error: suppliersError, SuppliersList } = useGetSuppliersList();
  const { PaymentTermsList, loading: termsLoading, error: termsError } = useGetPaymentTermsList();
  const { PaymentMethodList, loading: methodLoading, error: methodError } = useGetPaymentMethod();

  // إعداد Formik
  const formik = useFormik({
    initialValues: {
      distributedFlag: false, // هل الفاتورة موزعة أم لا
      invoiceNum: '',
      peopleId: '',
      // invoiceType: '',
      invoiceType: 'Standard',
      referenceType: '',
      referenceId: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      organizationId: '',
      paymentTermsId: '',
      paymentMethodId: '',
      currencyCode: 'SAR',
      currencyConversionRate: 1,
      includeVatFlag: true,
      apInvoiceLinesList: [],
      apPaymentSchedulesList: []
    },

    enableReinitialize: true,
    validationSchema: AddApInvoiceSchema(t, activeFinance),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        // التحقق من وجود بنود الفاتورة
        if (!values.apInvoiceLinesList?.length) {
          SwalAlert({ text: t('يجب إضافة بند واحد على الأقل إلى الفاتورة') });
          setSubmitting(false);
          return;
        }

        // التحقق من تطابق المبالغ
        const totalInvoiceLines = values.apInvoiceLinesList?.reduce((sum, item: any) => sum + (item.total || 0), 0);
        const totalPaymentSchedules = values.apPaymentSchedulesList?.reduce(
          (sum, payment: any) => sum + Number(payment.amount || 0),
          0
        );

        if (values.apPaymentSchedulesList?.length > 0 && totalInvoiceLines !== totalPaymentSchedules) {
          SwalAlert({ text: t('عفواً! هناك عدم تطابق في المبالغ. يرجى مراجعة القيم المدخلة.') });
          setSubmitting(false);
          return;
        }

        // إعداد البيانات للإرسال

        const submitData = {
          distributedFlag: values.distributedFlag,
          invoiceNum: values.invoiceNum,
          peopleId: values.peopleId,
          invoiceType: values.invoiceType,
          referenceType: values.referenceType,
          referenceId: values.referenceId || null,
          invoiceDate: values.invoiceDate,
          organizationId: values.organizationId,
          paymentTermsId: values.paymentTermsId,
          paymentMethodId: values.paymentMethodId,
          currencyCode: values.currencyCode,
          currencyConversionRate: values.currencyConversionRate || 1,
          includeVatFlag: values.includeVatFlag,
          apInvoiceLinesList: values.apInvoiceLinesList?.map((line: any, idx) => ({
            // lineNumber: line.lineNumber ,
            lineNumber: ++idx,
            lineType: line.lineType,
            invItemId: line.invItemId,
            uom: line.uom || null,
            description: line.description,
            price: line.price || 0,
            qty: line.qty || 0,
            discountRate: line.discountRate || 0,
            discountAmount: line.discountAmount || 0,
            vatRate: line.vatRate || 0,
            total: line.total,
            vatAmount: line.vatAmount || 0,
            accountId: line.accountId,
            costCenterId: line.costCenterId,

           
            branchId: line.branchId,
            companyId: line.companyId,
 
            currencyCode: line.currencyCode,
            currencyConversionRate: line.currencyConversionRate || 1,
            purHeaderId: line.purHeaderId,
            purLineId: line.purLineId
          })),
          apPaymentSchedulesList:
            values.apPaymentSchedulesList?.map((item: any) => ({
              amount: item.amount,
              dueDate: item.dueDate,
              paymentId: item.paymentId
            })) || null
        };

        // إرسال البيانات
        const result = await dispatch(addApInvoices(submitData)).unwrap();

        if (result?.succeeded) {
          CustomAlert({ action: 'Add' });
          resetForm();
          navigate('/AP/ApInvoices');
        } else {
          CustomAlert({ action: 'Error', msg: result?.message });
        }
      } catch (error) {
        CustomAlert({ action: 'Error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  // console.log(formik.values);

  const {
    hasPrepayments,
    totalPrepaymentAmount,
    UnApplyPrepaymentsList,
    loading: prepaymentsLoading,
    error: prepaymentsError
  } = useGetUnApplyPrepayments({
    PeopleId: formik.values.peopleId
  });

  const {
    rate,
    loading: loadingRate,
    error: errorRate,
    refetch
  } = useDailyRate({
    currencyCode: formik.values.currencyCode,
    date: formik.values.invoiceDate
  });

  // معالجة إرسال النموذج في المودال
  const handleSubmitModal = () => {
    if (selectedSupplier && selectedOperatingUnit && formik.values.invoiceType && selectedDistributedFlag !== null) {
      formik.setValues({
        ...formik.values,
        peopleId: selectedSupplier,
        organizationId: selectedOperatingUnit,
        distributedFlag: selectedDistributedFlag
      });
      setShowModal(false);
    } else {
      ToastSwal({ title: t('SelectBranchSupplierError'), timer: 1000 });
    }
  };

  // تغيير اللغات للنصوص في Select
  const getTranslatedText = (item: any) => {
    return i18n.language === 'ar' ? item.name2 || item.name : item.name || item.name2;
  };

  useEffect(() => {
    const supplier = SuppliersList.find(f => f.id === selectedSupplier);

    if (supplier) {
      formik.setFieldValue('paymentMethodId', supplier.paymentMethodId);
      // setFieldTouched('paymentMethodId', true);

      formik.setFieldValue('paymentTermsId', supplier.termsId);
      // setFieldTouched('paymentTermsId', true);

      formik.setFieldValue('currencyCode', supplier.invoiceCurrencyCode);
      // setFieldTouched('currencyCode', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSupplier, SuppliersList]);

  // حالة التحميل المجمعة
  const isLoading = currencyLoading || ouLoading || suppliersLoading || termsLoading || methodLoading;
  const hasError = currencyError || ouError || suppliersError || termsError || methodError;

  return (
    <>
      <Breadcrumb
        BreadcrumbList={[
          { name: 'Home', link: ':moduleHome' },
          { name: 'PaymentInvoices', link: '/AP/ApInvoices' },
          { name: 'AddNewInvoice', link: null }
        ]}
        PageName={t('AddNewInvoice')}
        subtitle={formik.values.distributedFlag ? 'فاتورة موزعة' : ''} // تحديث بناءً على boolean
      />

      <Card>
        <Loading loading={isLoading} error={hasError}>
          <Form onSubmit={formik.handleSubmit}>
            <CardBody>
              <div className='row'>
                {/* تاريخ الفاتورة */}
                <div className='col-lg-3 col-md-6 mb-3'>
                  <label className='form-label'>{t('Date')}</label>
                  <Form.Control
                    type='date'
                    name='invoiceDate'
                    onChange={formik.handleChange}
                    value={formik.values.invoiceDate}
                    isInvalid={formik.touched.invoiceDate && !!formik.errors.invoiceDate}
                  />
                  <Form.Control.Feedback type='invalid'>{formik.errors.invoiceDate}</Form.Control.Feedback>
                </div>

                {/* نوع الفاتورة */}
                <div className='col-lg-3 col-md-6 mb-3'>
                  <label className='form-label'>{t('InvoiceType')}</label>
                  <Form.Select
                    disabled={!showModal}
                    name='invoiceType'
                    onChange={formik.handleChange}
                    value={formik.values.invoiceType}
                    isInvalid={formik.touched.invoiceType && !!formik.errors.invoiceType}
                  >
                    <option value=''>{t('Choose')}</option>
                    <option value='Standard'>{t('Standard')}</option>
                    <option value='CreditMemo'>{t('CreditMemo')}</option>
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>{formik.errors.invoiceType}</Form.Control.Feedback>
                </div>

                {/* الوحدة التشغيلية */}
                <div className='col-lg-3 col-md-6 mb-3'>
                  <label className='form-label'>{t('OperatingUnit')}</label>
                  <Select
                    isDisabled
                    isLoading={ouLoading}
                    options={OperatingUnitOptions}
                    value={OperatingUnitOptions?.find((f: any) => f.value === formik.values.organizationId)}
                    placeholder={t('Choose')}
                    classNamePrefix='react-select'
                  />
                  <div className='text-danger small'>
                    {formik.touched.organizationId && formik.errors.organizationId}
                  </div>
                </div>

                {/* المورد */}
                <div className='col-lg-3 col-md-6 mb-3'>
                  <label className='form-label'>{t('Supplier')}</label>
                  <TreeSelect
                    disabled
                    loading={suppliersLoading}
                    className='w-100'
                    placeholder={t('Choose')}
                    value={formik.values.peopleId}
                    onChange={value => formik.setFieldValue('peopleId', value)}
                    treeData={treeSuppliersData}
                    treeNodeFilterProp='title'
                    showSearch
                    allowClear
                  />
                  <div className='text-danger small'>{formik.touched.peopleId && formik.errors.peopleId}</div>
                </div>

                {/* رقم فاتورة المورد */}
                <div className='col-lg-3 col-md-6 mb-3'>
                  <label className='form-label'>{t('SupplierInvoiceNumber')}</label>
                  <Form.Control
                    type='text'
                    name='invoiceNum'
                    onChange={formik.handleChange}
                    value={formik.values.invoiceNum}
                    isInvalid={formik.touched.invoiceNum && !!formik.errors.invoiceNum}
                    // placeholder={t('EnterInvoiceNumber')}
                  />
                  <Form.Control.Feedback type='invalid'>{formik.errors.invoiceNum}</Form.Control.Feedback>
                </div>

                {/* شروط الدفع */}
                <div className='col-lg-3 col-md-6 mb-3'>
                  <label className='form-label'>{t('PaymentTerms')}</label>
                  <Form.Select
                    name='paymentTermsId'
                    onChange={formik.handleChange}
                    value={formik.values.paymentTermsId}
                    isInvalid={formik.touched.paymentTermsId && !!formik.errors.paymentTermsId}
                  >
                    <option value=''>{t('Choose')}</option>
                    {PaymentTermsList?.map(item => (
                      <option key={item.id} value={item.id}>
                        {getTranslatedText(item)}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>{formik.errors.paymentTermsId}</Form.Control.Feedback>
                </div>

                {/* طريقة الدفع */}
                <div className='col-lg-2 col-md-6 mb-3'>
                  <label className='form-label'>{t('PaymentMethod')}</label>
                  <Form.Select
                    name='paymentMethodId'
                    onChange={formik.handleChange}
                    value={formik.values.paymentMethodId}
                    isInvalid={formik.touched.paymentMethodId && !!formik.errors.paymentMethodId}
                  >
                    <option value=''>{t('Choose')}</option>
                    {PaymentMethodList?.map((item: any) => (
                      <option key={item.id} value={item.id}>
                        {getTranslatedText(item)}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>{formik.errors.paymentMethodId}</Form.Control.Feedback>
                </div>

                {/* العملة */}
                <div className='col-lg-2 col-md-6 mb-3'>
                  <label className='form-label'>{t('Currency')}</label>
                  <Form.Select
                    name='currencyCode'
                    onChange={formik.handleChange}
                    value={formik.values.currencyCode}
                    isInvalid={formik.touched.currencyCode && !!formik.errors.currencyCode}
                  >
                    <option value=''>{t('Choose')}</option>
                    {Currency?.map((item: any) => (
                      <option key={item.currencyCode} value={item.currencyCode}>
                        {item.description}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>{formik.errors.currencyCode}</Form.Control.Feedback>
                </div>

                {/* سعر الصرف */}

                <div className='col-lg-2 '>
                  <div className='form-group mb-3'>
                    <label className='form-label'> {t('CurrencyConversionRate')} </label>
                    <InputGroup>
                      <Form.Control
                        className='form-control text-center'
                        type='text'
                        readOnly
                        value={loadingRate ? '' : errorRate ? 'فشل التحميل' : rate}
                        placeholder={loadingRate ? 'جاري التحميل...' : ''}
                      />

                      {loadingRate && (
                        <InputGroup.Text>
                          <div className='spinner-border spinner-border-sm text-primary'></div>
                        </InputGroup.Text>
                      )}

                      {errorRate && !loadingRate && (
                        <InputGroup.Text style={{ padding: '0' }}>
                          <button
                            type='button'
                            className='btn btn-outline-warning h-100 px-2'
                            onClick={refetch}
                            title='إعادة تحميل سعر الصرف'
                          >
                            <i className='fas fa-redo'></i>
                          </button>
                        </InputGroup.Text>
                      )}
                    </InputGroup>

                    {errorRate && !loadingRate && (
                      <small className='text-warning mt-1 d-block text-center'>
                        <i className='fas fa-exclamation-circle me-1'></i>
                        تعذر تحميل السعر
                        <button type='button' className='btn btn-link btn-sm p-0 text-warning ms-1' onClick={refetch}>
                          (إعادة المحاولة)
                        </button>
                      </small>
                    )}
                  </div>
                </div>
              </div>

              {/* تنبيه الدفعات المسبقة */}
              <Loading loading={prepaymentsLoading} error={prepaymentsError} Type='Dots'>
                {hasPrepayments && (
                  <div className='alert alert-info d-flex align-items-center m-0'>
                    <i className='fas fa-info-circle me-2'></i>
                    <div>
                      <strong>{t('Reminder')}</strong> {t('PrepaymentMessage')}
                      <span className='ms-2 text-success fw-bold'>
                        {totalPrepaymentAmount}
                        {/* {formik.values.currencyCode} */}
                      </span>
                    </div>
                  </div>
                )}
              </Loading>
            </CardBody>
            {/* التبويبات */}
            <CardFooter className='mt-0 p-0'>
              <ul className='nav nav-tabs p-0'>
                <li className='nav-item'>
                  <button
                    type='button'
                    className={`nav-link ${activeTab === 'apInvoiceLines' ? 'active' : ''}`}
                    onClick={() => setActiveTab('apInvoiceLines')}
                  >
                    <i className='fas fa-file-invoice me-2'></i>
                    {t('InvoiceLines')}
                  </button>
                </li>

                {formik.values.invoiceType !== 'CreditMemo' && (
                  <li className='nav-item'>
                    <button
                      type='button'
                      className={`nav-link ${activeTab === 'apPaymentSchedules' ? 'active' : ''}`}
                      onClick={() => setActiveTab('apPaymentSchedules')}
                    >
                      <i className='fas fa-calendar-alt me-2'></i>
                      {t('InvoiceSchedule')}
                    </button>
                  </li>
                )}
                {/* {formik.values.invoiceType === 'CreditMemo' && (
                  <li className='nav-item'>
                    <button
                      type='button'
                      className={`nav-link ${activeTab === 'debitMemoApplication' ? 'active' : ''}`}
                      onClick={() => setActiveTab('debitMemoApplication')}
                    >
                      <i className='fas fa-link me-2'></i>
                      {t('Apply Credit / إسناد الإشعار')}
                    </button>
                  </li>
                )} */}
              </ul>

              <div className='  py-4'>
                {activeTab === 'apInvoiceLines' && (
                  <div>
                    <ApInvoiceLines formik={formik} />
                    {/* {formik.errors.apInvoiceLinesList && (
                        <div className='text-danger small mt-2'>{t('InvoiceLinesRequired')}</div>
                      )} */}
                  </div>
                )}

                {activeTab === 'apPaymentSchedules' && (
                  <div>
                    <ApPaymentSchedules formik={formik} UnApplyPrepaymentsList={UnApplyPrepaymentsList} />
                    {formik.errors.apPaymentSchedulesList && (
                      <div className='text-danger small mt-2'>{t('InvoiceScheduleRequired')}</div>
                    )}
                  </div>
                )}

                {/* محتوى تبويب الإشعار الدائن */}
                {activeTab === 'debitMemoApplication' && formik.values.invoiceType === 'CreditMemo' && (
                  <div>
                    <ApDebitMemoApplication
                      formik={formik as any}
                      // outstandingInvoices={[]}
                      // loading={false}
                      // error={null}
                    />
                  </div>
                )}
              </div>
            </CardFooter>

            {/* أزرار الإجراءات */}
            <div className='d-flex justify-content-center gap-3 my-4'>
              <button type='submit' disabled={formik.isSubmitting} className='btn btn-theme btn-lg'>
                {formik.isSubmitting ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' />
                    {t('Processing')}
                  </>
                ) : (
                  <>
                    <i className='fas fa-plus me-2'></i>
                    {t('Add')}
                  </>
                )}
              </button>

              <button type='button' className='btn btn-secondary btn-lg' onClick={() => navigate('/AP/ApInvoices')}>
                <i className='fas fa-times me-2'></i>
                {t('Cancel')}
              </button>
            </div>
          </Form>
        </Loading>
      </Card>

      <Modal show={showModal} backdrop='static' keyboard={false} centered className='modal-selection-theme'>
        <Modal.Header className='bg-theme bg-opacity-20 text-dark py-3'>
          <Modal.Title className='text-center w-100 h5 mb-0'>
            <i className='fas fa-file-invoice me-2 text-theme'></i>
            {t('اختيار بيانات الفاتورة')}
          </Modal.Title>
        </Modal.Header>

        <Loading loading={ouLoading || suppliersLoading} error={ouError || suppliersError} Type='Dots'>
          <Modal.Body className='p-4'>
            <div className='text-center mb-4'>
              <div
                className='bg-theme bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3'
                style={{ width: '80px', height: '80px' }}
              >
                <i className='fas fa-clipboard-list fs-1 text-theme'></i>
              </div>
              <p className='text-muted mb-0'>{t('يجب اختيار الوحدة التشغيلية والمورد قبل المتابعة')}</p>
            </div>

            <div className='row'>
              <div className='col-12 mb-3'>
                <label className='form-label fw-bold'>{t('فاتورة موزعة')}</label>
                <div className='input-group'>
                  <span className='input-group-text bg-theme bg-opacity-10 border-end-0'>
                    <i className='fas fa-project-diagram text-theme'></i>
                  </span>
                  <div className='d-flex gap-2 p-2 border rounded flex-grow-1' style={{ minHeight: '44px' }}>
                    {/* لا - غير موزعة */}
                    <input
                      type='radio'
                      className='btn-check'
                      name='distributedFlag'
                      id='distributedNo'
                      checked={selectedDistributedFlag === false}
                      onChange={() => setSelectedDistributedFlag(false)}
                      autoComplete='off'
                    />
                    <label
                      className={`btn btn-sm ${
                        selectedDistributedFlag === false ? 'btn-theme' : 'btn-outline-theme'
                      } flex-fill d-flex align-items-center justify-content-center`}
                      htmlFor='distributedNo'
                    >
                      {t('No')}
                    </label>

                    {/* نعم - موزعة */}
                    <input
                      type='radio'
                      className='btn-check'
                      name='distributedFlag'
                      id='distributedYes'
                      checked={selectedDistributedFlag === true}
                      onChange={() => setSelectedDistributedFlag(true)}
                      autoComplete='off'
                    />
                    <label
                      className={`btn btn-sm ${
                        selectedDistributedFlag === true ? 'btn-theme' : 'btn-outline-theme'
                      } flex-fill d-flex align-items-center justify-content-center`}
                      htmlFor='distributedYes'
                    >
                      {t('Yes')}
                    </label>
                  </div>

                  {/* تلميح توضيحي */}
                  {/* {selectedDistributedFlag !== null && (
                    <div className='mt-2 small text-muted'>
                      <i className='fas fa-info-circle me-2 text-theme'></i>
                      {selectedDistributedFlag
                        ? 'فاتورة موزعة: سيتم توزيع المبالغ على مراكز التكلفة أو الحسابات المحددة في بنود الفاتورة.'
                        : 'فاتورة غير موزعة: لن يتم طلب توزيع إلزامي على مراكز التكلفة.'}
                    </div>
                  )} */}
                </div>
              </div>

              {/* الوحدة التشغيلية */}
              <div className='col-12 mb-4'>
                <label className='form-label fw-bold'>{t('OperatingUnit')}</label>
                <div className='input-group'>
                  <span className='input-group-text bg-theme bg-opacity-10 border-end-0'>
                    <i className='fas fa-building text-theme'></i>
                  </span>
                  <Select
                    classNamePrefix='react-select'
                    isLoading={ouLoading}
                    isSearchable={true}
                    isClearable
                    options={OperatingUnitOptions}
                    value={OperatingUnitOptions?.find((f: any) => f.value === selectedOperatingUnit)}
                    onChange={option => {
                      setSelectedOperatingUnit(option?.value || '');
                    }}
                    placeholder={t('اختر وحدة التشغيل')}
                    className='flex-grow-1'
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </div>
              </div>

              {/* المورد */}
              <div className='col-12 mb-4'>
                <label className='form-label fw-bold'>{t('Supplier')}</label>
                <div className='input-group '>
                  <span className='input-group-text bg-theme bg-opacity-10 border-end-0 '>
                    <i className='fas fa-truck-loading text-theme'></i>
                  </span>
                  <TreeSelect
                    loading={suppliersLoading}
                    className='flex-grow-1'
                    placeholder={t('اختر المورد')}
                    value={selectedSupplier}
                    onChange={setSelectedSupplier}
                    treeData={treeSuppliersData}
                    treeNodeFilterProp='title'
                    showSearch
                    allowClear
                    styles={{
                      popup: {
                        root: {
                          zIndex: 1055
                        }
                      }
                    }}
                  />
                </div>
              </div>

              <div className='col-12 mb-3'>
                <label className='form-label fw-bold'>{t('InvoiceType')}</label>
                <div className='input-group'>
                  <span className='input-group-text bg-theme bg-opacity-10 border-end-0'>
                    <i className='fas fa-receipt text-theme'></i>
                  </span>
                  <div className='d-flex gap-2 p-2 border rounded flex-grow-1 ' style={{ minHeight: '44px' }}>
                    {/* زر الفاتورة القياسية */}
                    <input
                      type='radio'
                      className='btn-check'
                      name='invoiceType'
                      id='invoiceTypeStandard'
                      value='Standard'
                      checked={formik.values.invoiceType === 'Standard'}
                      onChange={formik.handleChange}
                      autoComplete='off'
                    />
                    <label
                      className={`btn btn-sm ${
                        formik.values.invoiceType === 'Standard' ? 'btn-theme' : 'btn-outline-theme'
                      } flex-fill d-flex align-items-center justify-content-center`}
                      htmlFor='invoiceTypeStandard'
                      style={{ borderRadius: '1px', transition: 'all 0.3s ease' }}
                    >
                      {t('Standard')}
                    </label>

                    {/* زر إشعار مدين */}
                    <input
                      type='radio'
                      className='btn-check'
                      name='invoiceType'
                      id='invoiceTypeDebitMemo'
                      value='CreditMemo'
                      checked={formik.values.invoiceType === 'CreditMemo'}
                      onChange={formik.handleChange}
                      autoComplete='off'
                    />
                    <label
                      className={`btn btn-sm ${
                        formik.values.invoiceType === 'CreditMemo' ? 'btn-theme' : 'btn-outline-theme'
                      } flex-fill d-flex align-items-center justify-content-center`}
                      htmlFor='invoiceTypeDebitMemo'
                      style={{ borderRadius: '1px', transition: 'all 0.3s ease' }}
                    >
                      {t('DebitMemo')}
                    </label>
                  </div>

                  {/* ✅ Hint تحت اختيار نوع الفاتورة */}
                  {formik.values.invoiceType && (
                    <div className='mt-2 small text-muted'>
                      <i className='fas fa-info-circle me-2 text-theme'></i>

                      {formik.values.invoiceType === 'Standard'
                        ? 'فاتورة قياسية: لإنشاء فاتورة مشتريات/خدمات  من المورد.'
                        : 'إشعار مدين: لتخفيض فاتورة المورد (مرتجع/خصم من المورد).'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Modal.Body>
        </Loading>

        <Modal.Footer className='justify-content-center border-top-0 pt-0'>
          <button
            className='btn btn-outline-secondary btn-lg px-4 py-2 me-3 rounded-2'
            onClick={() => navigate('/AP/ApInvoices')}
          >
            <i className='fas fa-times me-2'></i>
            {t('Cancel')}
          </button>
          <button
            className='btn btn-theme btn-lg px-5 py-2 rounded-2'
            onClick={handleSubmitModal}
            disabled={!selectedSupplier || !selectedOperatingUnit || !formik.values.invoiceType}
          >
            <i className='fas fa-check-circle me-2'></i>
            {t('Confirm')}
          </button>
        </Modal.Footer>
      </Modal>

      <style>
        {`
          .ant-select-outlined:not(.ant-select-customize-input) .ant-select-selector {
              border: 1px solid var(--bs-border-color);
              border-radius: 3px !important;
              height: 2rem;
          }
          `}
      </style>
    </>
  );
};

export default AddApInvoice;
